<template>
  <section v-if="visible" class="cleaning-merge mb-5" :class="{ 'cleaning-merge--dark': isDarkMode }" aria-label="Reinigungsämtli zusammenführen">
    <div class="cleaning-merge__intro">
      <v-icon color="success" size="28">mdi-broom</v-icon>
      <div class="cleaning-merge__copy">
        <div class="font-weight-bold">Reinigungsämtli übersichtlich zusammenfassen</div>
        <div class="text-body-2 mt-1">Deine Reinigungsämtli werden Varianten unter «Reinigen». Beschreibungen, Planung und Erledigungen bleiben erhalten.</div>
        <div v-if="error && !dialog" class="text-body-2 mt-2" role="alert">{{ error }}</div>
      </div>
      <v-btn v-if="ready" color="success" depressed class="text-none" :disabled="loading" @click="dialog = true">Zusammenführen</v-btn>
      <v-btn v-else text color="primary" class="text-none" :loading="loading" @click="loadPreview()">Vorschau laden</v-btn>
    </div>

    <v-dialog v-model="dialog" max-width="680" :persistent="confirming" :fullscreen="$vuetify.breakpoint.xsOnly" :dark="isDarkMode" scrollable>
      <v-card :dark="isDarkMode">
        <v-card-title class="cleaning-merge__title">
          <div>
            <div class="text-h6 font-weight-bold">Unter «Reinigen» zusammenführen</div>
            <div v-if="preview" class="text-body-2 font-weight-regular mt-1">{{ preview.studioName }}</div>
          </div>
          <v-spacer />
          <v-btn icon :disabled="confirming" aria-label="Schliessen" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pt-3">
          <v-alert v-if="error" type="info" text dense role="alert">{{ error }}</v-alert>
          <v-progress-linear v-if="loading" indeterminate color="success" class="mb-4" />
          <template v-if="ready">
            <p v-if="preview.active">
              Diese <strong>{{ preview.active }} aktiven Reinigungsämtli</strong> werden als Varianten übernommen.
              Jede Variante behält ihre eigenen Einstellungen und Termine.
            </p>
            <p v-else>In deinem Studio ist aktuell kein Reinigungsämtli aktiviert. Unter «Reinigen» kannst du künftig eigene Varianten anlegen.</p>
            <p v-if="preview.inactiveRetained" class="text-body-2">
              {{ preview.inactiveRetained }} bereits angelegte, inaktive Varianten bleiben ebenfalls erhalten.
            </p>
            <ul v-if="preview.variants.length" class="cleaning-merge__variants">
              <li v-for="variant in preview.variants" :key="variant.templateId">
                <div>
                  <div class="font-weight-medium">{{ variant.variantName }}</div>
                  <div class="text-caption">{{ frequencyLabel(variant.frequency) }} · {{ variant.duration }} Min.</div>
                </div>
                <v-chip x-small :color="variant.active ? 'success' : undefined" outlined>{{ variant.active ? "Aktiv" : "Inaktiv" }}</v-chip>
              </li>
            </ul>
            <p class="mt-4 mb-0">
              Die bisherigen einzelnen Reinigungsvorlagen verschwinden aus dem Ämtli-Pool. Weitere Reinigungsaufgaben legst du direkt unter «Reinigen» als eigene Varianten an.
            </p>
            <p class="text-body-2 mt-3 mb-0">Die Zusammenführung gilt nur für {{ preview.studioName }}. Deine bisherigen Aufgaben und Erledigungen bleiben erhalten.</p>
          </template>
        </v-card-text>
        <v-divider />
        <v-card-actions class="cleaning-merge__actions pa-4">
          <v-btn text class="text-none" :disabled="confirming" @click="dialog = false">Später</v-btn>
          <v-spacer />
          <v-btn v-if="!ready" color="primary" text class="text-none" :loading="loading" @click="loadPreview()">Vorschau erneut laden</v-btn>
          <v-btn v-else color="success" depressed class="text-none" :loading="confirming" :disabled="loading" @click="confirmMerge">
            Bestätigen und zusammenführen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import api from "@/api"

export default {
  name: "WorkCalendarCleaningMerge",
  props: {
    studioId: { type: String, required: true },
    isDarkMode: { type: Boolean, default: false },
  },
  data() {
    return { preview: null, loading: true, confirming: false, dialog: false, error: "", requestId: 0, disposed: false }
  },
  computed: {
    ready() { return this.preview && this.preview.state === "ready" },
    visible() { return this.loading || Boolean(this.error) || this.ready },
  },
  mounted() { this.loadPreview() },
  beforeDestroy() { this.disposed = true; this.requestId += 1 },
  methods: {
    frequencyLabel(value) {
      return { daily: "Täglich", weekly: "Wöchentlich", monthly: "Monatlich", once: "Nur manuell" }[value] || "Nach Konfiguration"
    },
    async loadPreview(preserveError = false) {
      const requestId = ++this.requestId
      const studioId = this.studioId
      this.loading = true
      if (!preserveError) this.error = ""
      this.preview = null
      try {
        const preview = await api.shiftManagement.previewCleaningTaskGroup(studioId)
        if (this.disposed || requestId !== this.requestId || studioId !== this.studioId) return null
        this.preview = preview
        if (preview.state === "complete" || preview.state === "unavailable") this.error = ""
        return preview
      } catch (error) {
        if (!this.disposed && requestId === this.requestId) {
          this.error = error.response?.data?.message || "Die Vorschau konnte nicht geladen werden. Deine bisherigen Ämtli bleiben verfügbar."
        }
        return null
      } finally {
        if (!this.disposed && requestId === this.requestId) this.loading = false
      }
    },
    finishMerge(studioId) {
      if (this.disposed || studioId !== this.studioId) return
      this.dialog = false
      this.error = ""
      this.preview = { state: "complete", studioId }
      this.$emit("merged", { studioId })
    },
    async confirmMerge() {
      if (!this.ready || this.confirming || this.loading) return
      const studioId = this.studioId
      this.confirming = true
      this.error = ""
      try {
        const result = await api.shiftManagement.confirmCleaningTaskGroup(studioId, this.preview.previewToken)
        if (result.state === "complete" && result.studioId === studioId) this.finishMerge(studioId)
      } catch (error) {
        if (this.disposed || studioId !== this.studioId) return
        this.error = error.response?.data?.message || "Die Antwort konnte nicht empfangen werden. Wir prüfen den aktuellen Stand."
        // A lost response can occur after a successful commit. Re-read before
        // offering another confirmation; never repeat the write automatically.
        const preview = await this.loadPreview(true)
        if (preview?.state === "complete") this.finishMerge(studioId)
      } finally {
        if (!this.disposed) this.confirming = false
      }
    },
  },
}
</script>

<style scoped>
.cleaning-merge { padding: 18px 20px; border: 1px solid #bddcc7; border-radius: 14px; background: #f0f8f3; color: #244b32; }
.cleaning-merge--dark { background: #193326; border-color: #365d44; color: #e0efe5; }
.cleaning-merge__intro { display: flex; align-items: center; gap: 16px; }
.cleaning-merge__copy { flex: 1; }
.cleaning-merge__title { align-items: flex-start; flex-wrap: nowrap; word-break: normal; }
.cleaning-merge__variants { list-style: none; padding: 0; margin: 0; }
.cleaning-merge__variants li { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 0; border-bottom: 1px solid rgba(128, 128, 128, 0.2); }
.cleaning-merge__actions { flex-wrap: wrap; gap: 8px; }
@media (max-width: 600px) {
  .cleaning-merge__intro { flex-wrap: wrap; align-items: flex-start; gap: 12px; }
  .cleaning-merge__copy { flex-basis: calc(100% - 48px); }
  .cleaning-merge__intro > .v-btn { margin-left: 40px; }
  .cleaning-merge__actions > .v-btn:last-child { white-space: normal; min-height: 44px; height: auto; padding-top: 10px; padding-bottom: 10px; }
}
</style>
